import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { AuthLayout } from './AuthLayout';
import styles from './AuthLayout.module.css';
import { useAuth } from './useAuth';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { ApiError } from '@/lib/apiClient';

/**
 * Tela de acesso (RF-01.03).
 *
 * <p>
 * A mensagem de falha é <b>uma só</b>, e é assim de propósito: o servidor
 * devolve o mesmo 401 para e-mail inexistente, senha errada e conta bloqueada,
 * e a interface não deve inventar distinção que o servidor recusa a fazer
 * (docs/11 §2.2).
 * </p>
 */
export function LoginPage(): JSX.Element {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Para onde a pessoa estava indo antes de ser mandada para o login. O padrão
  // é a área logada (/app), não a raiz — a raiz agora é a landing pública.
  const destination = (location.state as { from?: string } | null)?.from ?? '/app';

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const session = await signIn(email, password);

      navigate(session.emailVerified ? destination : '/verificar-email', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.isUnauthenticated
          ? 'E-mail ou senha incorretos.'
          : 'Não foi possível entrar agora. Tente novamente em instantes.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthLayout
      footer={
        <>
          Ainda não tem conta? <Link to="/cadastro">Criar conta</Link>
        </>
      }
      subtitle="Entre para encontrar fornecedores e acompanhar suas solicitações."
      title="Entrar"
    >
      <form className={styles.form} noValidate onSubmit={(event) => void handleSubmit(event)}>
        {error !== null && <Alert tone="error">{error}</Alert>}

        <Field
          autoComplete="email"
          inputMode="email"
          label="E-mail"
          name="email"
          onChange={(event) => setEmail(event.target.value)}
          required
          type="email"
          value={email}
        />

        <Field
          autoComplete="current-password"
          label="Senha"
          name="password"
          onChange={(event) => setPassword(event.target.value)}
          required
          type="password"
          value={password}
        />

        <Button loading={isSubmitting} type="submit">
          Entrar
        </Button>

        <Link to="/esqueci-senha">Esqueci minha senha</Link>
      </form>
    </AuthLayout>
  );
}
